<template>
    <div class="file-question">
        <label><slot name="label"></slot></label>

        <div
            class="dropzone"
            :class="{ dragging: dragging, disabled: !canAddMore }"
            @dragenter.prevent="dragging = true"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
            @click="openPicker"
        >
            <span class="dropzone-icon">&#8593;</span>
            <span class="dropzone-title">Clique ou arraste seus arquivos aqui</span>
            <span class="dropzone-hint">{{ hint }}</span>
            <input
                ref="fileInput"
                type="file"
                :accept="accept"
                :multiple="multiple"
                @change="onPick"
            />
        </div>

        <div class="uploading" v-if="uploading">Enviando arquivos...</div>

        <ul class="file-list" v-if="files.length">
            <li v-for="(file, index) in files" :key="index">
                <a :href="file.url" target="_blank" rel="noopener noreferrer">{{
                    file.name
                }}</a>
                <button
                    type="button"
                    class="remove"
                    @click.prevent.stop="remove(index)"
                >
                    &times;
                </button>
            </li>
        </ul>

        <div class="error" v-if="error">{{ error }}</div>
    </div>
</template>
<script>
import keviApi from "../../keviApi";

const MAX_SIZE_MB = 30;
const ACCEPTED_EXTENSIONS = [
    "png", "jpg", "jpeg", "gif", "webp", "bmp", "tiff", "tif",
    "svg", "pdf", "ai", "eps", "cdr", "psd", "dxf",
];

export default {
    name: "file-question",
    props: {
        formId: [String, Number],
        linkCode: String,
        settings: {
            type: Object,
            default: function () {
                return {};
            },
        },
    },
    data: function () {
        return {
            files: [],
            uploading: false,
            dragging: false,
            error: null,
        };
    },
    computed: {
        multiple: function () {
            return this.settings && this.settings.multiple !== false;
        },
        maxFiles: function () {
            if (!this.multiple) {
                return 1;
            }
            return (this.settings && this.settings.maxFiles) || 10;
        },
        canAddMore: function () {
            return this.files.length < this.maxFiles;
        },
        accept: function () {
            return ACCEPTED_EXTENSIONS.map((ext) => "." + ext).join(",");
        },
        hint: function () {
            const formats = "PNG, JPG, SVG, PDF, AI, EPS, CDR, PSD";
            return `${formats} — até ${MAX_SIZE_MB} MB (máx. ${this.maxFiles})`;
        },
    },
    methods: {
        openPicker: function () {
            if (!this.canAddMore || this.uploading) {
                return;
            }
            this.$refs.fileInput.click();
        },
        onPick: function (event) {
            this.upload(Array.from(event.target.files || []));
            // Allows picking the same file again after removing it.
            event.target.value = "";
        },
        onDrop: function (event) {
            this.dragging = false;
            this.upload(Array.from(event.dataTransfer.files || []));
        },
        validate: function (file) {
            const extension = (file.name.split(".").pop() || "").toLowerCase();
            if (ACCEPTED_EXTENSIONS.indexOf(extension) === -1) {
                return `${file.name}: formato não aceito`;
            }
            if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
                return `${file.name}: excede ${MAX_SIZE_MB} MB`;
            }
            return null;
        },
        upload: function (selected) {
            if (!selected.length || this.uploading) {
                return;
            }

            this.error = null;
            const room = this.maxFiles - this.files.length;
            const accepted = [];

            for (const file of selected.slice(0, room)) {
                const validationError = this.validate(file);
                if (validationError) {
                    this.error = validationError;
                    continue;
                }
                accepted.push(file);
            }

            if (selected.length > room) {
                this.error = `Você pode enviar no máximo ${this.maxFiles} arquivo(s).`;
            }

            if (!accepted.length) {
                return;
            }

            const formData = new FormData();
            accepted.forEach((file) => formData.append("files[]", file));
            if (this.linkCode) {
                formData.append("link_code", this.linkCode);
            }

            this.uploading = true;
            const options = keviApi.getOptions(
                "form/" + this.formId + "/upload",
                "POST",
            );

            this.$http
                .post(options.url, formData)
                .then((response) => {
                    const uploaded = (response.body && response.body.files) || [];
                    this.files = this.files.concat(uploaded);
                    this.$emit("change", this.getValue());
                })
                .catch(() => {
                    this.error = "Não foi possível enviar os arquivos. Tente novamente.";
                })
                .finally(() => {
                    this.uploading = false;
                });
        },
        remove: function (index) {
            this.files.splice(index, 1);
            this.$emit("change", this.getValue());
        },
        focus: function () {
            this.$emit("focus", this);
        },
        getValue: function () {
            return this.files;
        },
        isValid: function () {
            return !this.uploading;
        },
        isNull: function () {
            return this.files.length === 0;
        },
    },
};
</script>
